import { ACTION } from '@configs/constants/action.constant';
import { environment } from '@environments/environment';
import { FuseCopierService } from '../../shared/@fuse/services/copier.service';
import { Injectable } from '@angular/core';
import { isNil } from 'lodash';
import { IError } from '../../../eightcap-core/api/models/error';
import { NotificationMessageType } from '@shared/models/notification-message-type';
import { Observable, from } from 'rxjs';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    constructor(private translateService: TranslateService, public fuseCopierService: FuseCopierService) {}

    BUTTON_COLOR = {
        BLUE: '#3085d6',
        RED: '#d33',
        GREEN: '#2dba6a'
    };

    alertConfirmation(type: string, message: string = null): Observable<SweetAlertResult> {
        const baseOptions: SweetAlertOptions = {
            title: this.translateService.instant('common.confirm_question'), // `Are you sure?`,
            text: message,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: this.BUTTON_COLOR.GREEN,
            confirmButtonText: this.getConfirmButtonTextByType(type)
        };

        return from(Swal.fire(baseOptions));
    }

    alertConfirmationDelete(content: string = null): Observable<SweetAlertResult> {
        const baseOptions: SweetAlertOptions = {
            title: this.translateService.instant('common.confirm_question'),
            text: isNil(content) ? this.translateService.instant('common.delete_warning') : content,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: this.BUTTON_COLOR.RED,
            confirmButtonText: this.translateService.instant('button_name.delete')
        };

        return from(Swal.fire(baseOptions));
    }

    alertConfirmationDeleteBulk(content: string = null): Observable<SweetAlertResult> {
        const baseOptions: SweetAlertOptions = {
            title: this.translateService.instant('common.confirm_question'),
            text: isNil(content) ? this.translateService.instant('common.delete_warning') : content,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: this.BUTTON_COLOR.RED,
            confirmButtonText: this.translateService.instant('button_name.delete')
        };

        return from(Swal.fire(baseOptions));
    }

    alertConfirmationApproveDenied(content: string, action = null): Observable<SweetAlertResult> {
        const baseOptions: SweetAlertOptions = {
            title: this.translateService.instant('common.confirm_question'),
            text: content,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: action === ACTION.DENY ? this.BUTTON_COLOR.RED : this.BUTTON_COLOR.GREEN,
            confirmButtonText:
                action === ACTION.DENY
                    ? this.translateService.instant('button_name.deny')
                    : this.translateService.instant('button_name.approve')
        };

        return from(Swal.fire(baseOptions));
    }

    notificationMessage(type: string, content: string): Observable<SweetAlertResult> {
        const baseOptions: SweetAlertOptions = {
            title:
                type === NotificationMessageType.SUCCESS
                    ? this.translateService.instant('common.notification.success')
                    : this.translateService.instant('common.notification.error'),
            text: content,
            confirmButtonColor:
                type === NotificationMessageType.SUCCESS ? this.BUTTON_COLOR.GREEN : this.BUTTON_COLOR.RED,
            icon:
                type === NotificationMessageType.SUCCESS
                    ? NotificationMessageType.SUCCESS
                    : NotificationMessageType.ERROR
        };

        return from(Swal.fire(baseOptions));
    }

    notifyText(content: string, type?: string): Observable<SweetAlertResult> {
        const baseOptions: SweetAlertOptions = {
            html: `<p>${content}</p>`,
            confirmButtonColor:
                type === NotificationMessageType.SUCCESS ? this.BUTTON_COLOR.GREEN : this.BUTTON_COLOR.RED
        };
        return from(Swal.fire(baseOptions));
    }

    notifySuccess(content: string): Observable<SweetAlertResult> {
        return this.notificationMessage(NotificationMessageType.SUCCESS, content);
    }

    notifyError(content: string): Observable<SweetAlertResult> {
        return this.notificationMessage(NotificationMessageType.ERROR, content);
    }

    getError(error: any): { message: string; errorObj: any } {
        const result = {
            message: '',
            errorObj: null
        };

        if (error) {
            // If the error object is returned from the server, it will contain error object, which has type of IError, in either error.error or error.error.error
            if (
                (error.error && error.error.message) ||
                (error.error && error.error.error && error.error.error.message)
            ) {
                // Check error.error.error first then error.error
                const errorObj: IError = error.error.error || error.error || '';

                if (errorObj) {
                    result.message = errorObj.message;
                    result.errorObj = errorObj;

                    const params = errorObj.info && errorObj.info.message_params;
                    if (params) {
                        params.forEach(
                            (value: string, index: any) =>
                                (result.message = result.message.replace(`{${index}}`, value))
                        );
                    }
                }
            } else {
                // This condition is for cases such as being unable to make request to the server
                result.message = error.message;
            }
        }

        return result;
    }

    copyText(text: any): void {
        this.fuseCopierService.copyText(text);
    }

    showError(error: any, request?: any): Observable<SweetAlertResult> {
        const err = this.getError(error);
        const html = `<p>${err.message}</p>
            <hr style="border: 0; border-top: 1px solid rgb(169, 169, 169);">
            <h2  class="swal2-title">   With 4 easy steps you can report this issue to our engineering team </h2>

            <ol type ="1" style="text-align: start;"> 
            <li><p>Click on Report to admin :<a href="mailto:${environment.supporterEmail}?subject=Admin error report&body=${err.message}"> Report to admin </a></p></li>
            <li><p>Click on Copy Request and paste it in the mail :
            <a href="javascript:void(0);" class="copyBtn"> Copy Request </a></p></li>
            <li><p>Click on Copy Payload and paste it in the mail :<a href="javascript:void(0);" class="copyBtn"> Copy Payload </a></p></li>
            <li><p>Click on Copy Stacktrace and paste it in the mail :<a href="javascript:void(0);" class="copyBtn"> Copy Stacktrace </a></p></li>
            </ol>`;

        const baseOptions: SweetAlertOptions = {
            title: this.translateService.instant('common.notification.error'),
            html,
            confirmButtonColor: this.BUTTON_COLOR.RED,
            icon: 'error',
            didOpen: (toast) => {
                const copyRequest =
                    toast.getElementsByClassName('copyBtn') && toast.getElementsByClassName('copyBtn')[0];
                const copyPayload =
                    toast.getElementsByClassName('copyBtn') && toast.getElementsByClassName('copyBtn')[1];
                const copyBtn = toast.getElementsByClassName('copyBtn') && toast.getElementsByClassName('copyBtn')[2];

                if (copyBtn) {
                    copyBtn.addEventListener('click', () => {
                        err.errorObj.debug
                            ? this.copyText(`stacktrace: ${err.errorObj.debug}`)
                            : this.copyText('stacktrace: NO STACKTRACE');
                    });
                }
                if (copyPayload) {
                    copyPayload.addEventListener('click', () => {
                        request?.body
                            ? this.copyText(
                                  `payload:  ${
                                      request?.body?.data ? JSON.stringify(request?.body?.data) : request?.body
                                  }`
                              )
                            : this.copyText('payload: NO PAYLOAD');
                    });
                }
                if (copyRequest) {
                    copyRequest.addEventListener('click', () => {
                        this.copyText(`request: ${error.url}`);
                    });
                }
            }
        };

        return from(Swal.fire(baseOptions));
    }

    showCommon(options: SweetAlertOptions): Observable<SweetAlertResult> {
        const baseOptions: SweetAlertOptions | any = {
            title: this.translateService.instant('common.notification.success'),
            confirmButtonColor: this.BUTTON_COLOR.GREEN,
            icon: 'success',
            ...options
        };

        return from(Swal.fire(baseOptions));
    }

    close(): void {
        return Swal.close();
    }

    private getConfirmButtonTextByType(type: string): string {
        let msg: string = 'button_name.update';

        if (type === NotificationMessageType.SAVE) {
            msg = 'button_name.save';
        }

        if (type === NotificationMessageType.SYNC) {
            msg = 'button_name.sync';
        }

        return this.translateService.instant(msg);
    }
}
